import { usePage } from '@inertiajs/react'
import { WifiOff, X } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'
import { useOnline } from '@/hooks/useOnline'
import { cx } from '@/lib/cx'
import { setLocale, t } from '@/lib/i18n'
import { dismissToast, useToasts } from '@/lib/toast'

/** Persistent layout around every page: language, toasts and the offline banner. */
export default function Layout({ children }: { children: ReactNode }) {
  const { locale } = usePage().props
  // Set during render (not in an effect) so the page below renders in the right language straight away.
  setLocale(locale)
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  return (
    <>
      <OfflineBanner />
      {children}
      <Toaster />
    </>
  )
}

function OfflineBanner() {
  const online = useOnline()
  if (online) return null
  return (
    <div className="pt-safe fixed inset-x-0 top-0 z-[60] bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
      <p className="flex items-center justify-center gap-2 px-4 py-1.5 text-sm font-medium">
        <WifiOff className="size-4" /> {t('common.offline')}
      </p>
    </div>
  )
}

function Toaster() {
  const toasts = useToasts()
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)_+_5.5rem)] z-[70] flex flex-col items-center gap-2 px-4 lg:bottom-6"
    >
      {toasts.map((item) => (
        <div
          key={item.id}
          className={cx(
            'pointer-events-auto flex max-w-full items-center gap-3 rounded-2xl py-2.5 pr-2 pl-4 text-sm font-medium shadow-lg sm:max-w-md',
            item.tone === 'error' ? 'bg-rose-600 text-white' : 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900',
          )}
        >
          <span className="min-w-0">{item.message}</span>
          {item.action && (
            <button
              className="shrink-0 rounded-lg px-2 py-1 font-semibold text-amber-300 dark:text-blue-600"
              onClick={() => {
                item.action!.onClick()
                dismissToast(item.id)
              }}
            >
              {item.action.label}
            </button>
          )}
          <button aria-label={t('common.dismiss')} className="shrink-0 rounded-lg p-1 opacity-60" onClick={() => dismissToast(item.id)}>
            <X className="size-4" />
          </button>
        </div>
      ))}
    </div>
  )
}
